import React, { MouseEvent, useState } from "react";
import { Text } from "@bookingcom/bui-react";
import useModalDialog from "../../../../../hooks/useModalDialog";
import { useViewPriceDetailsController } from "./ViewPriceDetails.controller";
import styles from "./ViewPriceDetails.module.css";

type ViewPriceDetailsProps = {
  children: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

export const ViewPriceDetails = ({ children, isOpen, onOpenChange }: ViewPriceDetailsProps) => {
  const [internalShowPriceDetails, setInternalShowPriceDetails] = useState<boolean>(false);
  const {
    text,
    modal: { title, closeAriaLabel }
  } = useViewPriceDetailsController();
  const Modal = useModalDialog();

  // Use external state if provided (lifted state pattern), otherwise use internal state
  const useExternalState = isOpen !== undefined && onOpenChange !== undefined;
  const showPriceDetails = useExternalState ? isOpen : internalShowPriceDetails;
  const setShowPriceDetails = useExternalState ? onOpenChange : setInternalShowPriceDetails;

  if (!text || !title || !closeAriaLabel || !children) return null;

  const handleClickViewPriceDetails = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowPriceDetails(true);
  };

  const handleCloseModal = () => {
    setShowPriceDetails(false);
  };

  return (
    <>
      <a className={styles.link} href="#" onClick={handleClickViewPriceDetails}>
        <Text variant="body_2">{text}</Text>
      </a>
      {/* Only render modal internally when not using external state */}
      {!useExternalState && (
        <Modal
          title={title}
          closeAriaLabel={closeAriaLabel}
          onCloseTrigger={handleCloseModal}
          active={showPriceDetails}
        >
          {children}
        </Modal>
      )}
    </>
  );
};

// Separate component for rendering ViewPriceDetails modal at parent level
export const ViewPriceDetailsModal = ({
  isOpen,
  onClose,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const {
    modal: { title, closeAriaLabel }
  } = useViewPriceDetailsController();
  const Modal = useModalDialog();

  if (!title || !closeAriaLabel) return null;

  return (
    <Modal title={title} closeAriaLabel={closeAriaLabel} onCloseTrigger={onClose} active={isOpen}>
      {children}
    </Modal>
  );
};
